import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, X, Trash2, ArrowRight, Mail } from "lucide-react";
import { formatDay } from "@/lib/format";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "converted", label: "Converted" },
  { key: "dismissed", label: "Dismissed" },
];

export default function InboxPage() {
  const [tab, setTab] = useState("pending");
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [converting, setConverting] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get(`/inbound-emails`, { params: { status_filter: tab } })
      .then(({ data }) => setEmails(data));
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/pipelines").then(({ data }) => setPipelines(data));
  }, []);

  const dismiss = async (email) => {
    if (!window.confirm(`Dismiss email from ${email.from_email}?`)) return;
    await api.post(`/inbound-emails/${email.email_id}/dismiss`);
    toast.success("Email dismissed");
    load();
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="cw-caption uppercase tracking-widest">Email Queue</div>
          <h1 className="cw-h1 mt-1 inline-flex items-center gap-2"><Inbox size={26} /> Inbox</h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Review parsed inbound pitch emails and convert them into deals or dismiss.
          </p>
        </div>
      </div>

      <div className="mt-6 border-b flex gap-1" style={{ borderColor: "var(--cw-border)" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              tab === t.key ? "" : "text-[var(--cw-muted)] border-transparent"
            }`}
            style={tab === t.key ? { color: "var(--cw-primary)", borderColor: "var(--cw-secondary)" } : {}}
            data-testid={`inbox-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 cw-card overflow-hidden">
        {emails.length === 0 ? (
          <div className="p-10 text-center text-[var(--cw-muted)]" data-testid="inbox-empty">
            No {tab} emails.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead style={{ background: "rgba(40,42,116,0.05)" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">From</th>
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">Sector</th>
                <th className="px-4 py-3 font-semibold">Round</th>
                <th className="px-4 py-3 font-semibold">Received</th>
                <th className="px-4 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((e) => (
                <tr key={e.email_id} className="border-t hover:bg-[rgba(40,42,116,0.03)]"
                  style={{ borderColor: "var(--cw-border)" }}
                  data-testid={`email-row-${e.email_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{e.from_name || e.from_email}</div>
                    <div className="text-xs text-[var(--cw-muted)]">{e.from_email}</div>
                  </td>
                  <td className="px-4 py-3 font-medium">{e.parsed_company_name || "—"}</td>
                  <td className="px-4 py-3 text-[var(--cw-muted)]">{e.parsed_sector || "—"}</td>
                  <td className="px-4 py-3">{e.parsed_round_size || "—"}</td>
                  <td className="px-4 py-3 text-[var(--cw-muted)]">{formatDay(e.received_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {tab === "pending" ? (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setSelectedEmail(e)}
                          className="cw-btn-primary text-xs inline-flex items-center gap-1"
                          data-testid={`convert-btn-${e.email_id}`}>
                          <ArrowRight size={12} /> Convert
                        </button>
                        <button onClick={() => dismiss(e)}
                          className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                          style={{ color: "var(--cw-error)", borderColor: "var(--cw-error)" }}
                          data-testid={`dismiss-btn-${e.email_id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ) : e.converted_deal_id ? (
                      <button onClick={() => nav(`/deals/${e.converted_deal_id}`)}
                        className="cw-btn-ghost text-xs" data-testid={`view-deal-${e.email_id}`}>
                        View Deal
                      </button>
                    ) : (
                      <span className="text-xs text-[var(--cw-muted)]">Dismissed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedEmail && (
        <ConvertModal
          email={selectedEmail}
          pipelines={pipelines}
          onClose={() => setSelectedEmail(null)}
          onConverted={(dealId, dup) => {
            setSelectedEmail(null);
            load();
            if (dup) {
              toast.warning(`Deal created but a possible duplicate was flagged`);
            } else {
              toast.success("Deal created from email");
            }
            nav(`/deals/${dealId}`);
          }}
        />
      )}
    </div>
  );
}

function ConvertModal({ email, pipelines, onClose, onConverted }) {
  const [pipelineId, setPipelineId] = useState(pipelines[0]?.pipeline_id || "");
  const fundId = pipelines.find((p) => p.pipeline_id === pipelineId)?.fund_id || "";
  const [form, setForm] = useState({
    company_name: email.parsed_company_name || "",
    company_domain: email.parsed_company_domain || "",
    sector: email.parsed_sector || "",
    round_size: email.parsed_round_size || "",
    founder_first_name: (email.parsed_founder_name || "").split(" ")[0] || "",
    founder_last_name: (email.parsed_founder_name || "").split(" ").slice(1).join(" "),
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/inbound-emails/${email.email_id}/convert`, {
        pipeline_id: pipelineId,
        fund_id: fundId,
        ...form,
      });
      onConverted(data.deal_id, data.duplicate_flagged);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to convert");
    } finally {
      setSubmitting(false);
    }
  };

  const inp = "w-full h-9 px-3 rounded-md border text-sm bg-white";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="convert-email-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
          <h2 className="cw-h2 inline-flex items-center gap-2"><Mail size={20} /> Convert to Deal</h2>
          <button type="button" onClick={onClose} data-testid="close-convert-modal"><X size={20} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 grid md:grid-cols-2 gap-6">
          {/* Original email */}
          <div className="cw-card p-4 bg-[rgba(40,42,116,0.03)]">
            <div className="cw-caption uppercase tracking-widest mb-2">Original Email</div>
            <div className="text-xs text-[var(--cw-muted)]">From</div>
            <div className="text-sm font-medium">{email.from_name || email.from_email}</div>
            <div className="text-xs text-[var(--cw-muted)]">{email.from_email}</div>
            <div className="mt-3 cw-caption">Subject</div>
            <div className="text-sm">{email.subject}</div>
            <div className="mt-3 cw-caption">Body</div>
            <div className="text-sm whitespace-pre-wrap text-[var(--cw-body)] max-h-64 overflow-y-auto">
              {email.body}
            </div>
          </div>

          {/* Confirm parsed deal */}
          <div className="space-y-3">
            <div className="cw-caption uppercase tracking-widest">Confirm Deal Details</div>
            <F label="Pipeline *">
              <select value={pipelineId} onChange={(e) => setPipelineId(e.target.value)} className={inp}
                style={{ borderColor: "var(--cw-border)" }} data-testid="convert-pipeline-select">
                {pipelines.map((p) => (
                  <option key={p.pipeline_id} value={p.pipeline_id}>{p.pipeline_name}</option>
                ))}
              </select>
            </F>
            <F label="Company Name *">
              <input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                className={inp} style={{ borderColor: "var(--cw-border)" }} data-testid="convert-company-input" />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Domain">
                <input value={form.company_domain} onChange={(e) => set("company_domain", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }} />
              </F>
              <F label="Sector">
                <input value={form.sector} onChange={(e) => set("sector", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }} />
              </F>
              <F label="Round Size">
                <input value={form.round_size} onChange={(e) => set("round_size", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }} placeholder="e.g. 5 Cr" />
              </F>
              <F label="Founder First Name *">
                <input required value={form.founder_first_name}
                  onChange={(e) => set("founder_first_name", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }}
                  data-testid="convert-founder-first-name" />
              </F>
              <F label="Founder Last Name">
                <input value={form.founder_last_name}
                  onChange={(e) => set("founder_last_name", e.target.value)}
                  className={inp} style={{ borderColor: "var(--cw-border)" }} />
              </F>
            </div>
          </div>
        </div>
        <div className="border-t px-6 py-4 flex justify-end gap-2" style={{ borderColor: "var(--cw-border)" }}>
          <button type="button" onClick={onClose} className="cw-btn-ghost">Cancel</button>
          <button type="submit" disabled={submitting || !pipelineId} className="cw-btn-primary"
            data-testid="submit-convert-btn">
            {submitting ? "Converting…" : "Create Deal"}
          </button>
        </div>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="cw-caption block mb-1">{label}</label>
      {children}
    </div>
  );
}
